import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { Lead } from "../api/types";
import {
  LEAD_INTEREST_LEVELS,
  LEAD_SOURCES,
  LEAD_STATUSES,
  leadInterestLabel,
  leadSourceLabel,
  leadStatusLabel,
} from "../lib/leads";

export function LeadDetailPage() {
  const { id } = useParams();
  const leadId = Number(id);

  const leadQ = useQuery({
    queryKey: ["lead", leadId],
    queryFn: () => api.leads.get(leadId),
    enabled: Number.isInteger(leadId),
  });

  if (leadQ.isLoading) {
    return (
      <Layout title="Lead">
        <p className="sub">Loading…</p>
      </Layout>
    );
  }
  if (leadQ.isError || !leadQ.data) {
    return (
      <Layout title="Lead">
        <section className="card">
          <p>That lead could not be found.</p>
          <Link to="/leads" className="btn btn-ghost btn-xl">
            Back to Leads
          </Link>
        </section>
      </Layout>
    );
  }

  return <LeadDetailView lead={leadQ.data.lead} />;
}

function LeadDetailView({ lead }: { lead: Lead }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [contactName, setContactName] = useState(lead.contactName);
  const [phone, setPhone] = useState(lead.phone ?? "");
  const [email, setEmail] = useState(lead.email ?? "");
  const [preferredMoveInTiming, setPreferredMoveInTiming] = useState(
    lead.preferredMoveInTiming ?? "",
  );
  const [unitPreferences, setUnitPreferences] = useState(lead.unitPreferences ?? "");
  const [inquirySource, setInquirySource] = useState(lead.inquirySource ?? "");
  const [status, setStatus] = useState(lead.status);
  const [followUpDate, setFollowUpDate] = useState(lead.followUpDate ?? "");
  const [tourScheduledAt, setTourScheduledAt] = useState(lead.tourScheduledAt ?? "");
  const [interestLevel, setInterestLevel] = useState(lead.interestLevel ?? "warm");
  const [contactOfficeIfOverdue, setContactOfficeIfOverdue] = useState(
    lead.contactOfficeIfOverdue,
  );
  const [notes, setNotes] = useState(lead.notes ?? "");
  const [saved, setSaved] = useState(false);

  const saveMut = useMutation({
    mutationFn: () =>
      api.leads.update(lead.id, {
        contactName: contactName.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        preferredMoveInTiming: preferredMoveInTiming.trim() || null,
        unitPreferences: unitPreferences.trim() || null,
        inquirySource: inquirySource || null,
        status,
        followUpDate: followUpDate || null,
        tourScheduledAt: tourScheduledAt || null,
        interestLevel,
        contactOfficeIfOverdue,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["lead", lead.id] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });

  return (
    <Layout title="Edit Lead">
      <section className="card form-card">
        <div className="detail-head">
          <div>
            <h2>{lead.contactName}</h2>
            <p className="sub">
              {lead.buildingName} · {leadSourceLabel(lead.inquirySource)} inquiry
            </p>
          </div>
        </div>

        <p className="help-line">Edit the lead's details and tap Save changes.</p>

        <label className="field">
          <span>Contact name</span>
          <input value={contactName} onChange={(e) => setContactName(e.target.value)} />
        </label>

        <div className="two-col">
          <label className="field">
            <span>Phone</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
          </label>
          <label className="field">
            <span>Email</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Tour scheduled</span>
            <input
              type="datetime-local"
              value={tourScheduledAt}
              onChange={(e) => setTourScheduledAt(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Activity / interest</span>
            <select value={interestLevel} onChange={(e) => setInterestLevel(e.target.value)}>
              {LEAD_INTEREST_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {leadInterestLabel(level)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="checkbox-line">
          <input
            type="checkbox"
            checked={contactOfficeIfOverdue}
            onChange={(e) => setContactOfficeIfOverdue(e.target.checked)}
          />
          <span>Contact office if overdue</span>
        </label>

        <div className="two-col">
          <label className="field">
            <span>Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {LEAD_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {leadStatusLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Follow-up date</span>
            <input
              type="date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
            />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Preferred move-in timing</span>
            <input
              value={preferredMoveInTiming}
              onChange={(e) => setPreferredMoveInTiming(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Inquiry source</span>
            <select value={inquirySource} onChange={(e) => setInquirySource(e.target.value)}>
              <option value="">Not set</option>
              {LEAD_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {leadSourceLabel(s)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="field">
          <span>Unit preferences</span>
          <input value={unitPreferences} onChange={(e) => setUnitPreferences(e.target.value)} />
        </label>

        <label className="field">
          <span>Notes</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        <div className="form-actions">
          {saved && !saveMut.isPending && <span className="saved-note">Saved.</span>}
          {saveMut.isError && <span className="field-error">Could not save. Try again.</span>}
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => navigate("/leads")}
            disabled={saveMut.isPending}
          >
            Back to Leads
          </button>
          <button
            className="btn btn-primary btn-xl"
            onClick={() => {
              setSaved(false);
              saveMut.mutate();
            }}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
